import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { LogoHorizontal } from '../components/Logo';
import { TextInput, PasswordInput } from '../components/Input';
import { PrimaryButton } from '../components/Button';
import { FormHeader, Divider } from '../components/Card';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';

export default function Login() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const familyCode = params.get('code') || '';
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const user = await login(email.trim().toLowerCase(), password);
            // If has family code in URL and not yet in a family, auto-join
            if (familyCode && !user.family_id) {
                try {
                    await api.post('/family/join', { family_code: familyCode });
                } catch (err) {
                    // Non-blocking
                }
            }
            // Decide where to go
            const me = await api.get('/auth/me').then((r) => r.data.user);
            if (!me.profile?.birthdate && !me.name) {
                navigate('/completar-perfil', { replace: true });
            } else if (!me.family_id) {
                navigate('/setup-family', { replace: true });
            } else {
                navigate('/home', { replace: true });
            }
        } catch (err) {
            const msg = formatApiError(err);
            if (err?.response?.status === 403 && msg.toLowerCase().includes('verifi')) {
                navigate(`/cuenta-no-verificada?email=${encodeURIComponent(email.trim().toLowerCase())}`);
                return;
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-surface app-shell flex flex-col py-8 px-6">
            <div className="flex justify-center mb-6 fade-up">
                <LogoHorizontal size="lg" />
            </div>

            <div className="bg-white rounded-[24px] shadow-card p-6 fade-up fade-up-delay-1">
                <FormHeader title="Iniciar Sesión" subtitle="Ingresa a tu cuenta para continuar" />

                <div className="grid grid-cols-2 gap-3 mb-1">
                    <button disabled className="h-12 bg-white border border-gray-200 rounded-2xl flex items-center justify-center gap-2 text-gray-400 text-sm font-semibold cursor-not-allowed opacity-60" data-testid="login-google-btn">
                        <span className="w-4 h-4 inline-block bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22><path fill=%22%234285F4%22 d=%22M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z%22/></svg>')] bg-contain bg-no-repeat" />Google
                    </button>
                    <button disabled className="h-12 bg-white border border-gray-200 rounded-2xl flex items-center justify-center gap-2 text-gray-400 text-sm font-semibold cursor-not-allowed opacity-60" data-testid="login-apple-btn">
                        Apple
                    </button>
                </div>
                <p className="text-center text-[11px] text-gray-400 mt-1.5">Próximamente</p>

                <Divider text="O utiliza tu correo electrónico" />

                <form onSubmit={submit} className="space-y-3">
                    <TextInput label="Correo electrónico" type="email" icon={Mail} placeholder="tu@correo.com" value={email} onChange={(e) => setEmail(e.target.value)} required testId="login-email-input" />
                    <PasswordInput label="Contraseña" icon={Lock} placeholder="Tu contraseña" value={password} onChange={(e) => setPassword(e.target.value)} required testId="login-password-input" />

                    <div className="text-right">
                        <button type="button" onClick={() => navigate('/recuperar-contrasena')} className="text-sm text-gray-500 hover:text-brand font-medium" data-testid="login-forgot-link">
                            ¿Olvidaste tu contraseña?
                        </button>
                    </div>

                    {error && <div className="text-red-500 text-sm font-medium" data-testid="login-error">{error}</div>}

                    <PrimaryButton type="submit" loading={loading} testId="login-submit-btn" className="mt-1">
                        Iniciar Sesión
                    </PrimaryButton>
                </form>

                <div className="text-center mt-4 text-sm text-gray-500">
                    ¿No tienes cuenta?{' '}
                    <button onClick={() => navigate(familyCode ? `/registro?code=${familyCode}` : '/registro')} className="text-brand-navy font-bold hover:underline" data-testid="login-register-link">
                        Regístrate
                    </button>
                </div>
            </div>
        </div>
    );
}
